import React from "react";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  Users, Zap, Square,
  Clock, Building2, RefreshCw, ArrowRight, Info, CheckCircle2,
} from "lucide-react";
import { useAuth } from "../../../contexts/AuthContext";
import { useDashTheme } from "../hooks/useDashTheme";
import { useOrgIntelStream } from "../hooks/useOrgIntelStream";
import { Avatar, Button } from "../components/primitives";
import { DateRangeTool, presetToRange } from "../components/DateRange";
import { ToolSelect } from "../components/ToolSelect";
import { Breadcrumb } from "../components/Breadcrumb";
import { ClassicMotion, Shim } from "../components/classic/micro";
import { ToolIcon, brandColorFor } from "../components/classic/panels";
import { toolGlyphIdFor } from "../components/classic/toolIcons";
import AILimitModal from "../../../components/AILimitModal/AILimitModal";
import { SERVER_URL } from "../../../utils/serverConfig";
import { inks, paper } from "./graphical/viz";
import { WowMotion } from "./graphical/WowMotion";

/* ─────────────────────────────────────────────────────────────────────────────
   Org Intelligence — command center (v5 UX, 2026-09-07).
   Type on the canvas, triage list + case beside it, severity as number +
   text band (never color-alone). No card rails, icon tiles, or chip soup.
   ───────────────────────────────────────────────────────────────────────────── */

const fmtDate = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
};
const fmtH = (h) => {
  const n = Number(h) || 0;
  if (n >= 10) return `${Math.round(n)}h`;
  const hh = Math.floor(n); const mm = Math.round((n - hh) * 60);
  return hh ? (mm ? `${hh}h ${mm}m` : `${hh}h`) : `${mm}m`;
};
const PATHISH = /(?:[A-Za-z]:\\|\\\\|\/(?:Users|home|var|tmp|mnt|opt|Volumes)\/)/i;
const looksLikePath = (s) => PATHISH.test(String(s || "")) || (String(s || "").match(/[\\/]/g) || []).length >= 2;
const shortApp = (name) => String(name || "").replace(/\s+\d{4}\b.*$/, "").replace(/^Adobe\s+/i, "").trim();
const workNoun = (tool) => {
  const t = String(tool || "").toLowerCase();
  if (/premiere|after effects|media encoder|davinci|final cut/.test(t)) return "Repeat edit";
  if (/photoshop|illustrator|indesign|figma|canva|sketch/.test(t)) return "Repeat design";
  if (/excel|sheets|numbers|csv/.test(t)) return "Spreadsheet repeat";
  if (/whatsapp|slack|teams|telegram|discord/.test(t)) return "Chat thread";
  if (/chrome|safari|firefox|edge|brave|arc/.test(t)) return "Browser repeat";
  if (/cursor|visual studio|code|intellij|pycharm|webstorm|xcode/.test(t)) return "Repeat coding";
  if (/word|docs|notion|pages/.test(t)) return "Document repeat";
  const s = shortApp(tool);
  return s ? `Repeat ${s}` : "Repeat work";
};
const displayAutoTitle = (a) => {
  const raw = String(a?.title || "").trim();
  if (raw && !looksLikePath(raw) && raw.length <= 48 && !/^[A-Za-z]:/.test(raw)) return raw;
  return workNoun(a?.tool);
};
const alpha = (hex, a) => {
  if (!hex || hex[0] !== "#") return hex;
  const n = parseInt(hex.slice(1, 7), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
};
function usePalette() {
  const t = useDashTheme();
  const k = inks(t);
  const dark = t.isDark;
  return React.useMemo(() => ({
    t, k, ppr: paper(t),
    red: dark ? "#DA4F4F" : "#E5484D",
    green: dark ? "#2F9E52" : "#50AB5B",
    orange: dark ? "#B88C00" : "#EDA100",
    bandColor: (band) => (band === "act" ? (dark ? "#DA4F4F" : "#E5484D") : band === "week" ? (dark ? "#B88C00" : "#EDA100") : band === "monitor" ? (dark ? "#3987E5" : "#4273D1") : (dark ? "#2F9E52" : "#50AB5B")),
    bandInk: (band) => (band === "act" ? (dark ? "#F0A3A3" : "#B3261E") : band === "week" ? (dark ? "#E3C36A" : "#8A6116") : band === "monitor" ? (dark ? "#A3C4F2" : "#2C4E93") : (dark ? "#8FD6A4" : "#2E6B38")),
    bandLabel: (band) => (band === "act" ? "Act now" : band === "week" ? "This week" : band === "monitor" ? "Monitor" : "Minor"),
  }), [t, k, dark]);
}

/* ── shared chrome ──────────────────────────────────────────────────────── */
/** The "i" on every section. Renders through a portal so a card's
 *  overflow:hidden can never clip it; opens on hover or click, closes on
 *  outside click / Esc; flips above the trigger when there is no room. */
function InfoTip({ text, title = "What this means" }) {
  const t = useDashTheme();
  const k = inks(t);
  const [open, setOpen] = React.useState(false);
  const [pos, setPos] = React.useState(null);
  const btnRef = React.useRef(null);
  const place = React.useCallback(() => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const W = 320; const pad = 12;
    let left = Math.min(Math.max(pad, r.left - 8), window.innerWidth - W - pad);
    const below = r.bottom + 10; const roomBelow = window.innerHeight - below;
    setPos({ left, top: roomBelow > 180 ? below : null, bottom: roomBelow > 180 ? null : window.innerHeight - r.top + 10, W });
  }, []);
  const show = () => { place(); setOpen(true); };
  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    const onDown = (e) => { if (btnRef.current && !btnRef.current.contains(e.target) && !e.target.closest?.("[data-oi3-tip]")) setOpen(false); };
    window.addEventListener("keydown", onKey); document.addEventListener("mousedown", onDown); window.addEventListener("scroll", place, true); window.addEventListener("resize", place);
    return () => { window.removeEventListener("keydown", onKey); document.removeEventListener("mousedown", onDown); window.removeEventListener("scroll", place, true); window.removeEventListener("resize", place); };
  }, [open, place]);
  return (
    <span style={{ position: "relative", display: "inline-flex", marginLeft: 6, verticalAlign: "middle" }}>
      <button ref={btnRef} type="button" aria-label="What is this?" aria-expanded={open}
        onMouseEnter={show} onMouseLeave={() => setOpen(false)} onClick={(e) => { e.stopPropagation(); if (open) setOpen(false); else show(); }}
        style={{ border: `1px solid ${open ? k.blue : "transparent"}`, background: open ? alpha(k.blue, 0.12) : "transparent", padding: 2, borderRadius: 99, cursor: "help", display: "inline-flex", color: k.blue, lineHeight: 0 }}>
        <Info size={13} />
      </button>
      {open && pos ? ReactDOM.createPortal(
        <div data-oi3-tip role="tooltip" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)} style={{
          position: "fixed", left: pos.left, top: pos.top ?? "auto", bottom: pos.bottom ?? "auto", width: pos.W, zIndex: 4000,
          background: t.c.surface, color: t.c.text, border: `1px solid ${t.c.border}`, borderRadius: 12, boxShadow: t.shadow.lg,
          padding: "12px 14px", fontFamily: t.font.family, textTransform: "none", letterSpacing: 0, textAlign: "left", whiteSpace: "normal",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
            <span style={{ width: 22, height: 22, borderRadius: 7, display: "grid", placeItems: "center", background: alpha(k.blue, 0.12), color: k.blue }}><Info size={12} /></span>
            <span style={{ ...t.font.statLabel, color: k.blue, fontSize: 10 }}>{title}</span>
          </div>
          <div style={{ fontSize: 12.5, lineHeight: 1.6, fontWeight: 400 }}>{text}</div>
          <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${t.c.border}`, fontSize: 11, color: t.c.textSecondary }}>Measured from tracked activity · nothing is invented</div>
        </div>, document.body) : null}
    </span>
  );
}
function Chip({ tone, dot, children, onClick, title }) {
  const { t, ppr } = usePalette();
  return (
    <span onClick={onClick} title={title} style={{
      display: "inline-flex", alignItems: "center", gap: 7, whiteSpace: "nowrap", fontSize: 12.5, fontWeight: 650, padding: "6px 13px", borderRadius: 999,
      background: tone ? alpha(tone, 0.13) : ppr.well, border: `1px solid ${tone ? alpha(tone, 0.4) : t.c.border}`, color: tone || t.c.textSecondary,
      fontVariantNumeric: "tabular-nums", cursor: onClick ? "pointer" : "default",
    }}>
      {dot ? <i aria-hidden="true" style={{ width: 7, height: 7, borderRadius: 99, background: dot, flexShrink: 0 }} /> : null}
      {children}
    </span>
  );
}
function Pill({ c, children, title }) {
  return (
    <span title={title} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5, fontWeight: 700, padding: "4px 10px", borderRadius: 999, background: alpha(c, 0.13), color: c, border: `1px solid ${alpha(c, 0.38)}`, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
      <i aria-hidden="true" style={{ width: 6, height: 6, borderRadius: 99, background: c }} />{children}
    </span>
  );
}
function Card({ children, style, pad = "22px 26px 24px", className }) {
  const { t, ppr } = usePalette();
  return (
    <div className={className} style={{ position: "relative", background: ppr.card, border: `1px solid ${t.c.border}`, borderRadius: 16, boxShadow: t.shadow.raised, padding: pad, ...style }}>
      {children}
    </div>
  );
}
function SecHead({ kicker, title, right, tip }) {
  const { t } = usePalette();
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: t.c.textTertiary }}>{kicker}{tip ? <InfoTip text={tip} /> : null}</div>
        <h2 style={{ margin: "6px 0 0", fontSize: 18, fontWeight: 700, letterSpacing: "-0.35px", lineHeight: 1.2, color: t.c.text }}>{title}</h2>
      </div>
      {right ? <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>{right}</div> : null}
    </div>
  );
}
function Hrs({ n }) {
  const { green } = usePalette();
  return (
    <span aria-hidden="true" style={{
      width: 38, height: 38, borderRadius: 11, flexShrink: 0, display: "grid", placeItems: "center",
      fontSize: 13, fontWeight: 750, fontVariantNumeric: "tabular-nums",
      background: alpha(green, 0.14), color: green, border: `1px solid ${alpha(green, 0.38)}`,
    }}>{n}</span>
  );
}
function Sev({ band, n }) {
  const { bandColor, bandInk } = usePalette();
  const c = bandColor(band); const ink = bandInk(band);
  return (
    <span aria-hidden="true" style={{
      width: 38, height: 38, borderRadius: 11, flexShrink: 0, display: "grid", placeItems: "center",
      fontSize: 14, fontWeight: 750, fontVariantNumeric: "tabular-nums",
      background: alpha(c, 0.14), color: ink, border: `1px solid ${alpha(c, 0.38)}`,
    }}>{n}</span>
  );
}
function BandMark({ band }) {
  const { bandColor, bandInk, bandLabel } = usePalette();
  return (
    <span style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: bandInk(band), display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
      <i aria-hidden="true" style={{ width: 8, height: 8, borderRadius: 99, background: bandColor(band) }} />
      {bandLabel(band)}
    </span>
  );
}
function Label({ c, children, right }) {
  const { t } = usePalette();
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "14px 0 8px" }}>
      <span style={{ width: 3, height: 12, borderRadius: 2, background: c }} />
      <span style={{ ...t.font.microLabel, color: t.c.text }}>{children}</span>
      {right ? <span style={{ marginLeft: "auto" }}>{right}</span> : null}
    </div>
  );
}
function Bar({ c, pct, h = 6 }) {
  return (
    <span style={{ display: "block", height: h, borderRadius: 999, background: alpha(c, 0.14), overflow: "hidden" }}>
      <span style={{ display: "block", height: "100%", width: `${Math.max(2, Math.min(100, pct))}%`, background: c, borderRadius: 999, transition: "width .5s cubic-bezier(.16,1,.3,1)" }} />
    </span>
  );
}
function Tool({ name, size = 20, c }) {
  const t = useDashTheme();
  if (name && (toolGlyphIdFor(name) || brandColorFor(name, t))) return <span title={name} style={{ display: "inline-flex", flexShrink: 0 }}><ToolIcon name={name} size={size} /></span>;
  return <span title={name} style={{ width: size, height: size, borderRadius: 6, display: "grid", placeItems: "center", background: c || t.c.primary, color: "#fff", fontSize: 9, fontWeight: 800, flexShrink: 0 }}>{String(name || "?").slice(0, 2)}</span>;
}
/** Text the model is writing right now, with a blinking caret. */
function Caret({ c }) {
  return <span aria-hidden="true" style={{ display: "inline-block", width: 2, height: "1em", background: c, marginLeft: 3, verticalAlign: "-0.15em", animation: "oi3blink 0.9s steps(2, start) infinite" }} />;
}
function typingFor(typing, kind, id, field) {
  if (!typing || typing.kind !== kind || (id || null) !== (typing.id || null)) return null;
  if (field && typing.field !== field) return null;
  return typing;
}
function Landing({ children, on = true, delay = 0 }) {
  return <div className={on ? "tgw-a" : undefined} style={{ animationDelay: on ? `${delay}ms` : undefined }}>{children}</div>;
}
function Skel({ lines = 3 }) {
  return <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>{Array.from({ length: lines }, (_, i) => <Shim key={i} h={12} w={`${88 - i * 14}%`} />)}</div>;
}
/* Skeleton kit — every placeholder has the same bones as the thing it stands
   in for, so nothing jumps when the real frame lands. */
function SkChips({ n = 3, w = 96 }) {
  return <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>{Array.from({ length: n }, (_, i) => <Shim key={i} h={26} w={w + (i % 3) * 22} r={999} />)}</div>;
}
function SkHead({ kicker = 160, title = 220 }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 14 }}>
      <Shim w={34} h={34} r={10} />
      <div style={{ flex: 1 }}><Shim h={9} w={kicker} r={5} /><Shim h={16} w={title} style={{ marginTop: 7 }} /></div>
    </div>
  );
}
function SkRows({ n = 3, h = 52, avatar = true }) {
  const t = useDashTheme();
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {Array.from({ length: n }, (_, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 12, border: `1px solid ${t.c.border}`, height: h }}>
          {avatar ? <Shim w={28} h={28} r={99} /> : <Shim w={28} h={28} r={9} />}
          <div style={{ flex: 1 }}><Shim h={12} w={`${58 - (i % 3) * 10}%`} /><Shim h={9} w={`${40 + (i % 2) * 12}%`} style={{ marginTop: 6 }} /></div>
          <Shim w={72} h={22} r={999} />
        </div>
      ))}
    </div>
  );
}
function SkBars({ n = 5 }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
      {Array.from({ length: n }, (_, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Shim w={22} h={22} r={6} /><Shim w={120} h={12} /><div style={{ flex: 1 }}><Shim h={6} r={999} w={`${90 - i * 14}%`} /></div><Shim w={44} h={12} />
        </div>
      ))}
    </div>
  );
}
function SkLabel() { return <div style={{ margin: "14px 0 8px" }}><Shim h={9} w={120} r={5} /></div>; }

/* ── generation bar ─────────────────────────────────────────────────────── */
function GenBar({ steps, status, streaming, cached, report }) {
  const { t, k, green, ppr } = usePalette();
  const total = status?.steps || steps[steps.length - 1]?.steps || 6;
  const done = !streaming && report;
  const label = cached ? `Cached · generated ${cached.ageMinutes < 1 ? "just now" : `${cached.ageMinutes} min ago`}` : done ? `Generated in ${((report.generationMs || 0) / 1000).toFixed(1)} s` : "Live · generating";
  const items = Array.from({ length: total }, (_, i) => {
    const s = steps.find((x) => x.step === i + 1);
    const cur = streaming && status && status.step === i + 1;
    const fin = s && (!cur || done) && (done || (status && status.step > i + 1));
    return { i: i + 1, s, cur: cur && !done, fin: done || fin };
  });
  if (done && !streaming) return null;
  return (
    <div className="oi3-pulse" style={{ marginTop: 16, alignItems: "flex-start" }}>
      <i className="oi3-dot" style={{ background: k.violet, animation: "oi3blink 0.9s steps(2, start) infinite" }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12.5, color: t.c.textSecondary }}>{label}{status?.ms != null ? ` · ${(status.ms / 1000).toFixed(1)} s` : ""}</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
          {items.map((it) => (
            <span key={it.i} title={it.s?.detail || ""} style={{
              fontSize: 12, fontWeight: 600, color: it.fin ? t.c.textSecondary : it.cur ? k.violet : t.c.textTertiary,
              display: "inline-flex", alignItems: "center", gap: 6,
            }}>
              {it.fin ? <CheckCircle2 size={12} style={{ color: green }} /> : it.cur ? <i style={{ width: 10, height: 10, borderRadius: 99, border: `2px solid ${k.violet}`, borderRightColor: "transparent", animation: "oi3spin 1s linear infinite" }} /> : <i style={{ width: 8, height: 8, borderRadius: 99, border: `1.5px solid ${t.c.border}` }} />}
              {it.s?.message || `Step ${it.i}`}
            </span>
          ))}
        </div>
        {status?.detail ? <div style={{ marginTop: 6, fontSize: 12, color: t.c.textTertiary }}>{status.detail}</div> : null}
      </div>
      <span style={{ display: "none" }}>{ppr.well}</span>
    </div>
  );
}

/* ── brief + health ─────────────────────────────────────────────────────── */
function FactLine({ text }) {
  const s = String(text || "");
  const re = /(\d+(?:\.\d+)?\s?h(?:ours?)?(?:\/day)?|[+\-−▼▲↑↓]\s?\d+(?:\.\d+)?%)/g;
  const out = [];
  let last = 0;
  let m;
  while ((m = re.exec(s))) {
    if (m.index > last) out.push(s.slice(last, m.index));
    out.push(<strong key={m.index} className="oi3-stat">{m[0]}</strong>);
    last = m.index + m[0].length;
  }
  if (last < s.length) out.push(s.slice(last));
  return out.length ? out : s;
}
function parseBeat(s, i) {
  const raw = String(s || "").trim();
  const rules = [
    [/^(biggest risk)\s*[:—–-]\s*/i, "act", "Biggest risk"],
    [/^(overload cost|overload)\s*[:—–-]\s*/i, "act", "Overload"],
    [/^(biggest strength|strength)\s*[:—–-]\s*/i, "ok", "Strength"],
    [/^(sharp drops? to watch|sharp drops?|drops? to watch)\s*[:—–-]\s*/i, "week", "Sharp drops"],
    [/^(what changed|changed)\s*[:—–-]\s*/i, "monitor", "Changed"],
  ];
  for (const [re, band, label] of rules) {
    if (re.test(raw)) return { band, label, body: raw.replace(re, "") };
  }
  return { band: "monitor", label: String(i + 1).padStart(2, "0"), body: raw };
}
function Brief({ ai, blockers, report, streaming, typing }) {
  const { t, k, green, bandColor, bandInk } = usePalette();
  const headline = ai.headline || report?.headline || null;
  const liveHead = !headline ? typingFor(typing, "headline", null, "text") : null;
  const beats = ai.beats || report?.beats || null;
  const aiWritten = Boolean(ai.headline) || Boolean(report?.aiWritten);
  return (
    <Card pad="24px 26px 22px">
      <div className="oi3-ai-flag">AI read · the period in one line</div>
      {headline ? (
        <p className="oi3-read-head"><FactLine text={headline} /></p>
      ) : liveHead ? (
        <p className="oi3-read-head">{liveHead.text}<Caret c={k.violet} /></p>
      ) : (
        <div style={{ marginTop: 14 }}><Shim h={20} w="88%" /><Shim h={20} w="58%" style={{ marginTop: 8 }} /><div style={{ fontSize: 12.5, color: t.c.textSecondary, marginTop: 10 }}>{streaming ? "Facts first — the headline lands when the model finishes it." : "Run an analysis to read the period."}</div></div>
      )}
      {headline ? (
        <p className="oi3-read-meta">
          {aiWritten ? "Written from the work" : "Composed from the figures"}
          {blockers?.analysed ? ` · ${Number(blockers.analysed.evidence || 0).toLocaleString()} entries` : ""}
          · words only — numbers stay measured
        </p>
      ) : null}
      {beats && beats.length ? (
        <ol className="oi3-beats">
          {beats.map((b, i) => {
            const beat = parseBeat(b, i);
            return (
              <li key={i} className="oi3-beat">
                <span className="oi3-beat-k" style={{ color: bandInk(beat.band) }}>
                  <i aria-hidden="true" style={{ background: bandColor(beat.band) }} />
                  {beat.label}
                </span>
                <span className="oi3-beat-b"><FactLine text={beat.body} /></span>
              </li>
            );
          })}
        </ol>
      ) : streaming ? (
        <ol className="oi3-beats">
          {[0, 1, 2, 3].map((i) => <li key={i} className="oi3-beat"><Shim h={9} w={72} /><Shim h={12} w="92%" style={{ marginTop: 10 }} /><Shim h={12} w="70%" style={{ marginTop: 6 }} /></li>)}
        </ol>
      ) : null}
      <p className="oi3-prov"><i className="oi3-dot" style={{ background: green }} />Measured from tracked activity. The model cannot add, drop or re-rank anything.</p>
    </Card>
  );
}
function Health({ health, changes }) {
  const { t, k, green, red, orange, bandInk, bandColor } = usePalette();
  if (!health) {
    return (
      <Card pad="24px 26px 22px">
        <div className="oi3-h" style={{ marginTop: 0 }}>Company health</div>
        <div className="oi3-health-stack">
          <Shim w={128} h={128} r={99} />
          <div style={{ width: "100%" }}>{[0, 1, 2, 3].map((i) => <div key={i} className="oi3-hrow"><Shim h={12} w={110} /><Shim h={12} w={28} /></div>)}</div>
        </div>
      </Card>
    );
  }
  const score = health.score;
  const band = score >= 70 ? "ok" : score >= 45 ? "week" : "act";
  const c = bandColor(band);
  const word = score >= 70 ? "Healthy" : score >= 45 ? "Watch" : "Needs work";
  const delta = changes?.healthDelta;
  const prev = delta != null ? score - delta : null;
  const pen = health.penalties || {};
  const rows = [
    { l: "Coverage", v: `${health.parts.coveragePct}%`, pen: Number(pen.coverage) || 0 },
    { l: "Blockers", v: String(health.parts.blockers), pen: Number(pen.blockers) || 0 },
    { l: "Quiet", v: String(health.parts.quiet), pen: Number(pen.quiet) || 0 },
    { l: "Trend", v: health.parts.trendPct == null ? "—" : `${health.parts.trendPct > 0 ? "▲" : health.parts.trendPct < 0 ? "▼" : "▬"} ${Math.abs(health.parts.trendPct)}%`, pen: Number(pen.trend) || 0 },
  ];
  const totalPen = rows.reduce((s, r) => s + r.pen, 0);
  const worst = rows.reduce((a, r) => (r.pen > a.pen ? r : a), rows[0]);
  const barMax = Math.max(36, ...rows.map((r) => r.pen), 1);
  const r = 52;
  const circ = 2 * Math.PI * r;
  return (
    <Card pad="24px 26px 22px">
      <div className="oi3-h" style={{ marginTop: 0 }}>
        Company health
        <InfoTip title="How it's scored" text="Starts at 100. Coverage gap × 0.35, blocker weight (max 36), quiet people × 6 (max 18), and a negative hours trend × 0.4 (max 12) come off. Same formula every period, so 52 now means the same as 52 last month." />
      </div>
      <div className="oi3-health-stack">
        <p className="oi3-health-word" style={{ color: bandInk(band) }}>{word}</p>
        {delta != null ? (
          <p className="oi3-health-delta">{delta >= 0 ? "▲" : "▼"} {Math.abs(delta)} vs last period{prev != null ? ` · was ${prev}` : ""}</p>
        ) : null}
        <svg className="oi3-health-ring" width="132" height="132" viewBox="0 0 132 132" role="img" aria-label={`Health ${score} of 100 — ${word}`}>
          <circle cx="66" cy="66" r={r} fill="none" stroke={alpha(c, 0.16)} strokeWidth="12" />
          <circle cx="66" cy="66" r={r} fill="none" stroke={c} strokeWidth="12" strokeDasharray={`${(score / 100) * circ} ${circ}`} strokeLinecap="round" transform="rotate(-90 66 66)" />
          <text x="66" y="62" textAnchor="middle" fontSize="34" fontWeight="750" fill={t.c.text} fontFamily={t.font.family}>{score}</text>
          <text x="66" y="82" textAnchor="middle" fontSize="11" fontWeight="600" fill={t.c.textTertiary} fontFamily={t.font.family}>of 100</text>
        </svg>
        <div className="oi3-hrows" aria-label="What came off the score">
          <div className="oi3-hrow oi3-hrow-cap"><span>Taken off 100</span><span>pts</span></div>
          {rows.map((row) => (
            <div key={row.l} className="oi3-hpen">
              <div className="oi3-hrow">
                <span className="l">{row.l} <b>{row.v}</b></span>
                <span className="n" style={{ color: row.pen ? red : t.c.textTertiary }}>{row.pen ? `−${row.pen}` : "0"}</span>
              </div>
              <span className="oi3-htrack" aria-hidden="true"><i style={{ width: `${Math.max(row.pen ? 6 : 0, (row.pen / barMax) * 100)}%`, background: c }} /></span>
            </div>
          ))}
        </div>
        <p className="oi3-hfoot">{totalPen ? `${totalPen.toFixed(totalPen % 1 ? 1 : 0)} pts off 100${worst.pen ? ` · ${worst.l.toLowerCase()} took most` : ""}.` : "Nothing came off 100 this period."} Same formula every period.</p>
      </div>
      <span style={{ display: "none" }}>{k.blue}{green}{orange}</span>
    </Card>
  );
}

/* ── what changed ───────────────────────────────────────────────────────── */
function Changes({ changes, ready, pending }) {
  const { t, k, green, red, ppr } = usePalette();
  if (!ready) {
    if (!pending) return null;
    return (
      <Card c={k.blue} style={{ marginTop: 14 }}>
        <SecHead c={k.blue} kicker="Since the previous period" title="What changed" right={<Shim h={26} w={150} r={999} />} />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 10 }}>
          {[0, 1, 2, 3].map((i) => <div key={i} style={{ padding: "11px 13px", borderRadius: 12, border: `1px solid ${t.c.border}`, display: "flex", flexDirection: "column", gap: 8 }}><Shim h={22} w={84} r={999} /><Shim h={13} w="70%" /><Shim h={10} w="50%" /></div>)}
        </div>
      </Card>
    );
  }
  const cards = [];
  if (changes) {
    for (const b of changes.newBlockers.slice(0, 2)) cards.push({ c: red, tag: "new blocker", title: b.identifier, sub: `since ${b.sinceLabel || "this period"} · ${b.people} ${b.people === 1 ? "person" : "people"}` });
    for (const b of changes.resolved.slice(0, 2)) cards.push({ c: green, tag: "resolved", title: b.identifier, sub: `was ${b.prevHits}× on ${b.prevDays} days · 0 this period` });
    for (const q of changes.newQuiet.slice(0, 2)) cards.push({ c: k.gold, tag: "went quiet", title: q.name, sub: `last activity ${q.lastActiveLabel}` });
    for (const d of changes.deptFlips.slice(0, 2)) cards.push({ c: k.teal, tag: "trend flip", title: `${d.name} ${d.trendPct > 0 ? "▲" : "▼"} ${Math.abs(d.trendPct)}%`, sub: `was ${d.prevTrendPct > 0 ? "+" : ""}${d.prevTrendPct}% the period before` });
    if (changes.hoursDelta != null && Math.abs(changes.hoursDelta) >= 4) cards.push({ c: k.blue, tag: "hours", title: `${changes.hoursDelta > 0 ? "+" : ""}${changes.hoursDelta}h tracked`, sub: `vs ${changes.comparedTo.label}` });
  }
  return (
    <Card c={k.blue} style={{ marginTop: 14 }}>
      <SecHead c={k.blue} kicker="Since the previous period" title="What changed"
        tip="Compared against the report for the same-length window immediately before this one. The first report for a scope has nothing to compare with yet."
        right={changes ? <Chip>compared to {changes.comparedTo.label}</Chip> : null} />
      {!changes ? (
        <div style={{ fontSize: 13, color: t.c.textSecondary, display: "flex", gap: 8, alignItems: "center" }}><i style={{ width: 7, height: 7, borderRadius: 99, background: k.blue }} />First report for this scope — the comparison starts with the next period's report.</div>
      ) : !cards.length ? (
        <div style={{ fontSize: 13, color: t.c.textSecondary, display: "flex", gap: 8, alignItems: "center" }}><i style={{ width: 7, height: 7, borderRadius: 99, background: green }} />Nothing moved: same blockers, same people, same trend as {changes.comparedTo.label}.</div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 10 }}>
          {cards.slice(0, 6).map((cd, i) => (
            <div key={i} className="tgw-a" style={{ padding: "11px 13px", borderRadius: 12, border: `1px solid ${t.c.border}`, display: "flex", flexDirection: "column", gap: 6 }}>
              <Pill c={cd.c}>{cd.tag}</Pill>
              <b style={{ fontSize: 13.5, letterSpacing: "-0.2px" }}>{cd.title}</b>
              <span style={{ fontSize: 12, color: t.c.textSecondary }}>{cd.sub}</span>
            </div>
          ))}
        </div>
      )}
      <span style={{ display: "none" }}>{ppr.well}</span>
    </Card>
  );
}

/* ── KPI row ────────────────────────────────────────────────────────────── */
function Kpi({ c, label, value, unit, sub, tip, children }) {
  const { t } = usePalette();
  return (
    <Card c={c} pad="14px 16px">
      <div style={{ ...t.font.statLabel, color: t.c.textSecondary, display: "flex", alignItems: "center" }}>{label}{tip ? <InfoTip text={tip} /> : null}</div>
      <div style={{ fontSize: 30, fontWeight: 750, letterSpacing: "-1.2px", lineHeight: 1, marginTop: 8, fontVariantNumeric: "tabular-nums" }}>{value}{unit ? <span style={{ fontSize: 14, fontWeight: 700, color: t.c.textSecondary, letterSpacing: 0 }}>{unit}</span> : null}</div>
      {sub ? <div style={{ fontSize: 12, marginTop: 4, color: t.c.textSecondary, fontWeight: 600 }}>{sub}</div> : null}
      <div style={{ marginTop: 10 }}>{children}</div>
    </Card>
  );
}
function KpiRow({ m, blockers, pending }) {
  const { t, k, green, red, orange, ppr } = usePalette();
  if (!m) {
    if (!pending) return null;
    const cs = [k.blue, k.teal, red, k.gold, k.violet, k.pink];
    const labels = ["Tracked hours", "People active", "Verified blockers", "Went quiet", "Working days", "Hours at risk"];
    return (
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginTop: 14 }}>
        {labels.map((l, i) => (
          <Card key={l} c={cs[i]} pad="14px 16px">
            <div style={{ ...t.font.statLabel, color: t.c.textSecondary }}>{l}</div>
            <Shim h={30} w={72} style={{ marginTop: 8 }} /><Shim h={10} w="70%" style={{ marginTop: 8 }} />
            <div style={{ marginTop: 10 }}>{i === 0 ? <div style={{ display: "flex", gap: 3, alignItems: "flex-end", height: 28 }}>{[60, 80, 50, 90, 70, 20, 10].map((h, j) => <Shim key={j} h={Math.round(28 * h / 100)} r={3} style={{ flex: 1 }} />)}</div> : i === 3 ? <div style={{ display: "flex" }}>{[0, 1].map((j) => <Shim key={j} w={26} h={26} r={99} style={{ marginLeft: j ? -8 : 0 }} />)}</div> : i === 4 ? <div style={{ display: "flex", gap: 3 }}>{[0, 1, 2, 3, 4, 5, 6].map((j) => <Shim key={j} h={22} r={6} style={{ flex: 1 }} />)}</div> : <Shim h={6} r={999} />}</div>
          </Card>
        ))}
      </div>
    );
  }
  const kp = m.kpis;
  const trend = kp.hoursTrendPct;
  const maxH = Math.max(...m.hoursByDay.map((d) => d.hours), 1);
  const quiet = m.peopleSignals.quiet;
  const bl = blockers?.blockers || [];
  const risk = blockers?.hoursAtRisk;
  const bandC = (b) => (b.severityBand === "act" ? red : b.severityBand === "week" ? orange : b.severityBand === "monitor" ? k.gold : green);
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginTop: 14 }}>
      <Kpi c={k.blue} label="Tracked hours" value={fmtH(kp.totalHours).replace("h", "")} unit="h" tip="Per person per day: the app's own session clock when it exists, otherwise measured active time. Never both added."
        sub={<span style={{ color: trend == null ? t.c.textSecondary : trend >= 0 ? green : red }}>{trend == null ? "no full prior period to compare" : `${trend >= 0 ? "▲" : "▼"} ${Math.abs(trend)}% vs last period`}</span>}>
        <div style={{ display: "flex", gap: 3, alignItems: "flex-end", height: 28 }}>
          {m.hoursByDay.map((d) => <b key={d.date} title={`${d.weekday} ${d.date} · ${fmtH(d.hours)}`} style={{ flex: 1, maxWidth: 24, height: `${Math.max(8, (d.hours / maxH) * 100)}%`, background: d.isWorkday ? k.blue : alpha(k.blue, 0.22), borderRadius: "3px 3px 0 0" }} />)}
        </div>
      </Kpi>
      <Kpi c={k.teal} label="People active" value={kp.peopleActive} unit={` / ${kp.peopleExpected}`} tip="People with any tracked activity in the period, against those expected to track (anyone with data in the last 30 days)."
        sub={`${m.coverage.notExpected.count} not expected to track`}>
        <Bar c={k.teal} pct={kp.coveragePct} />
      </Kpi>
      <Kpi c={red} label="Verified blockers" value={bl.length} tip="A blocker needs strong failure or waiting phrasing, a concrete identifier, and the same identifier on 2+ separate days."
        sub={blockers ? `${fmtH(risk)} touched · ${new Set(bl.flatMap((b) => b.people.map((p) => p.id))).size} people` : "verifying…"}>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {bl.slice(0, 4).map((b) => <Bar key={b.id} c={bandC(b)} pct={b.severity} h={5} />)}
          {!bl.length && blockers ? <Pill c={green}>all clear</Pill> : null}
        </div>
      </Kpi>
      <Kpi c={k.gold} label="Went quiet" value={quiet.length} tip="Had 2+ active days, then nothing across the final 2+ complete working days." sub="had data, then stopped">
        <div style={{ display: "flex" }}>{quiet.slice(0, 5).map((q, i) => <span key={q.id} style={{ marginLeft: i ? -8 : 0 }}><Avatar name={q.name} size={26} /></span>)}</div>
      </Kpi>
      <Kpi c={k.violet} label="Working days" value={kp.workingDays} unit={` of ${m.period.daysInRange}`} tip="Detected from the organisation's own daily volume; Mon–Fri when the period is too thin to vote." sub="detected from org activity">
        <div style={{ display: "flex", gap: 3 }}>
          {m.period.workdayFlags.slice(0, 14).map((d) => <span key={d.date} title={d.date} style={{ flex: 1, maxWidth: 24, height: 22, borderRadius: 5, display: "grid", placeItems: "center", fontSize: 10, fontWeight: 700, background: d.isWorkday ? t.c.text : ppr.well, color: d.isWorkday ? ppr.card : t.c.textTertiary }}>{d.weekday}</span>)}
        </div>
      </Kpi>
      <Kpi c={k.pink} label="Hours at risk" value={risk == null ? "—" : fmtH(risk).replace("h", "")} unit={risk == null ? "" : "h"} tip="Distinct hours in which a verified blocker was hit, summed across blockers. Share is against total tracked hours."
        sub={risk != null && kp.totalHours ? `${((risk / kp.totalHours) * 100).toFixed(1)}% of the period, inside blockers` : "inside verified blockers"}>
        <Bar c={k.pink} pct={risk != null && kp.totalHours ? (risk / kp.totalHours) * 100 : 0} />
      </Kpi>
    </div>
  );
}

function splitClauses(text) {
  return String(text || "")
    .replace(/\s+/g, " ")
    .split(/\s*;\s+|(?<=[.!?])\s+(?=[A-Z0-9“"])/)
    .map((s) => s.replace(/\s+/g, " ").trim().replace(/[.;]+$/, ""))
    .filter((s) => s.length > 8);
}
function uniquePoints(list, cap = 8) {
  const out = []; const seen = new Set();
  for (const p of list) {
    if (!p) continue;
    const k = String(p).toLowerCase().replace(/[^a-z0-9]+/g, " ").trim().slice(0, 48);
    if (!k || seen.has(k)) continue;
    seen.add(k);
    out.push(p);
    if (out.length >= cap) break;
  }
  return out;
}
const WEAK_WHERE = /^(media player|applicationframehost|chrome|edge|safari|brave|firefox|antigravity|windows|desktop|explorer|msedge)$/i;
function restatesFacts(s, b) {
  const k = String(s || "").toLowerCase();
  if (/^total\b/.test(k) && /\boccurrences?\b|\bhours?\b/.test(k)) return true;
  if (/\d+\s+occurrences?.+\d+\s+hours?\s+touched/.test(k)) return true;
  if (/\d+\s+hours?\s+(?:lost|touched).+\d+\s+people/.test(k) && !/absorbed|already-reduced/.test(k)) return true;
  const names = (b.people || []).filter((p) => k.includes(String(p.name).toLowerCase())).length;
  if (names >= 2 && /hit it \d+|times over|twice in/.test(k)) return true;
  return false;
}
function evidenceSummary(b) {
  const ident = b.identifier || String(b.title || "").split(/[—–|-]/)[0].trim();
  const apps = (b.apps || []).map((a) => a.app).filter((a) => a && !WEAK_WHERE.test(a)).slice(0, 3);
  const what = [
    ident,
    b.kind === "waiting" ? "waiting" : "blocked work",
    apps.length ? apps.join(", ") : null,
    b.daysHit ? `${b.daysHit} days` : null,
    b.sinceLabel || null,
  ].filter(Boolean).join(" · ");
  const who = (b.people || []).map((p) => `${p.name.split(" ")[0]} · ${p.hits}× · ${fmtH(p.hoursCaught)}`);
  const extra = uniquePoints([...splitClauses(b.problem), ...splitClauses(b.impact)], 8)
    .filter((s) => !restatesFacts(s, b))
    .filter((s) => {
      const k = s.toLowerCase();
      if (ident && k.includes(String(ident).toLowerCase()) && /unreachable|non-functional|became/.test(k)) return false;
      return s.length <= 180;
    })
    .slice(0, 2);
  return uniquePoints([what, ...who, ...extra], 8);
}
function hitBits(row) {
  const first = String(row.name || "").split(" ")[0];
  const what = splitClauses(row.what).slice(0, 1);
  const where = row.where && !WEAK_WHERE.test(row.where)
    && row.what && !String(row.what).toLowerCase().includes(String(row.where).toLowerCase().slice(0, 12))
    ? row.where : null;
  const notes = String(row.note || "").split(/\s*·\s*/).map((s) => s.trim()).filter(Boolean).filter((n) => {
    const k = n.toLowerCase();
    const w = String(row.what || "").toLowerCase();
    if (/hit it too/.test(k)) return false;
    if (first && k.includes(first.toLowerCase()) && k.length < 28) return false;
    if (/retried|same sitting/.test(k) && /retried|same sitting/.test(w)) return false;
    if (/same place/.test(k) && /same (place|location)/.test(w)) return false;
    return true;
  });
  return uniquePoints([...what, where, ...notes], 3);
}
function Points({ items, live, c }) {
  if (!items.length && !live) return null;
  return (
    <ul className="oi3-pts">
      {items.map((p, i) => <li key={`${i}-${p.slice(0, 24)}`}><FactLine text={p} /></li>)}
      {live ? <li>{live}<Caret c={c} /></li> : null}
    </ul>
  );
}

/* ── evidence trail (time · person · tool · what happened) ─────────────── */
function EvidenceTrail({ b }) {
  const { t } = usePalette();
  const rows = (b.ledger && b.ledger.length)
    ? b.ledger
    : (b.proof || []).map((o, i) => ({
      ...o,
      what: (typeof (b.evidence || [])[i] === "string" && !/^\d+ occurrences/.test(b.evidence[i])) ? b.evidence[i] : o.what,
    }));
  if (rows.length) {
    return (
      <div className="oi3-trail">
        {rows.map((row, i) => {
          const bits = hitBits(row);
          return (
            <div key={`${row.at || i}-${row.userId || i}`} className="oi3-led">
              <span className="oi3-led-i">{i + 1}</span>
              <div style={{ minWidth: 0 }}>
                <div className="oi3-led-h">
                  <b>{row.label || "—"}</b>
                  {row.name ? <span> · {String(row.name).split(" ")[0]}</span> : null}
                  {row.app ? <span className="oi3-led-app"> · {row.app}</span> : null}
                </div>
                {bits.length ? (
                  <ul className="oi3-led-pts">
                    {bits.map((p) => <li key={p}>{p}</li>)}
                  </ul>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    );
  }
  const lines = uniquePoints((b.evidence || []).filter((e) => e && !/^\d+ occurrences/.test(e)));
  if (!lines.length) {
    return <div style={{ fontSize: 12.5, color: t.c.textSecondary }}>No analysed hits on this blocker yet.</div>;
  }
  return <Points items={lines} />;
}

/* ── blocker desk: list + case file, one sheet ─────────────────────────── */
function BlockerDesk({ list, selectedId, onSelect, streaming, typing, selected, m, onOpenPerson, pending }) {
  const { t, k, green, bandInk } = usePalette();
  const caseRef = React.useRef(null);
  const listRef = React.useRef(null);
  const sorted = React.useMemo(() => [...list].sort((a, b) => b.severity - a.severity), [list]);
  const pick = (id) => {
    onSelect(id);
    if (typeof window !== "undefined" && window.matchMedia("(max-width: 980px)").matches) {
      caseRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  };
  const onListKey = (e) => {
    if (!sorted.length) return;
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp" && e.key !== "Home" && e.key !== "End") return;
    e.preventDefault();
    const i = Math.max(0, sorted.findIndex((b) => b.id === selectedId));
    const next = e.key === "Home" ? 0 : e.key === "End" ? sorted.length - 1 : e.key === "ArrowDown" ? Math.min(sorted.length - 1, i + 1) : Math.max(0, i - 1);
    if (sorted[next]) {
      pick(sorted[next].id);
      listRef.current?.querySelectorAll("[data-bid]")[next]?.focus();
    }
  };
  const clear = !streaming && !list.length;
  return (
    <div className="oi3-desk">
      <aside className="oi3-desk-list" ref={listRef} role="listbox" aria-label="Verified blockers" tabIndex={0} onKeyDown={onListKey}>
        {list.length ? (
          sorted.map((b) => {
            const sel = b.id === selectedId;
            const tt = !b.aiWritten ? typingFor(typing, "blocker", b.id, "title") : null;
            return (
              <button key={b.id} type="button" data-bid={b.id} role="option" aria-selected={sel} className={`oi3-brow${sel ? " on" : ""}`} onClick={() => pick(b.id)}>
                <Sev band={b.severityBand} n={b.severity} />
                <span style={{ minWidth: 0 }}>
                  <span className="oi3-brow-t">{tt ? <>{tt.text}<Caret c={bandInk(b.severityBand)} /></> : b.title}</span>
                  <span className="oi3-brow-m">{b.occurrences}× on {b.daysHit} days · {fmtH(b.hoursCaught)} touched{b.status === "new" ? " · new" : b.status === "recurring" ? " · recurring" : ""}</span>
                  <span className="oi3-brow-side">
                    <BandMark band={b.severityBand} />
                    <span className="oi3-faces">{b.people.slice(0, 3).map((p) => <Avatar key={p.id} name={p.name} size={22} />)}</span>
                  </span>
                </span>
              </button>
            );
          })
        ) : clear ? (
          <div style={{ padding: "22px 20px", fontSize: 13.5, lineHeight: 1.55, color: t.c.textSecondary }}>
            <b style={{ display: "block", color: green, marginBottom: 4 }}>Nothing recurred across days</b>
            No identifier came back on two or more separate days. That is a good period.
          </div>
        ) : (
          <div style={{ padding: "16px 20px" }}><SkRows n={4} h={56} avatar={false} /><div style={{ fontSize: 12, color: t.c.textSecondary, marginTop: 10 }}>Verifying — same identifier on 2+ days</div></div>
        )}
      </aside>
      <article ref={caseRef} className="oi3-desk-case" aria-live="polite">
        <Proof embedded b={selected} m={m} onOpenPerson={onOpenPerson} typing={typing} pending={pending} />
      </article>
      <span style={{ display: "none" }}>{k.violet}</span>
    </div>
  );
}
function Proof({ b, m, onOpenPerson, typing, pending, embedded }) {
  const { t, k, green, bandColor, bandInk, bandLabel, ppr } = usePalette();
  const live = b && !b.aiWritten ? typingFor(typing, "blocker", b.id) : null;
  const liveField = (f) => (live && live.field === f ? live.text : null);
  const shell = (children) => (embedded ? <div>{children}</div> : <Card pad="0">{children}</Card>);
  if (!b && pending) {
    return shell(
      <>
        <div className="oi3-case-h"><Shim w={38} h={38} r={11} /><div style={{ flex: 1 }}><Shim h={20} w="62%" /><Shim h={12} w="40%" style={{ marginTop: 8 }} /></div></div>
        <div className="oi3-facts">{[0, 1, 2, 3].map((i) => <div key={i}><Shim h={18} w={48} /><Shim h={10} w={64} style={{ marginTop: 6 }} /></div>)}</div>
        <div className="oi3-proof" style={{ padding: "20px 26px 26px" }}><SkLabel /><Shim h={64} /><SkLabel /><SkRows n={2} h={44} /></div>
      </>
    );
  }
  if (!b) {
    return shell(
      <div style={{ padding: "28px 26px" }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: t.c.textTertiary }}>Case file</div>
        <h3 style={{ margin: "8px 0 0", fontSize: 20, fontWeight: 700, letterSpacing: "-0.35px" }}>Select a blocker</h3>
        <p style={{ margin: "8px 0 0", fontSize: 13.5, lineHeight: 1.55, color: t.c.textSecondary, maxWidth: "46ch" }}>Every row is verified: the same identifier on 2+ separate days. The case opens beside the row — when, who, the evidence trail, the next step.</p>
      </div>
    );
  }
  const c = bandColor(b.severityBand);
  const ink = bandInk(b.severityBand);
  const maxDay = Math.max(...b.byDay.map((d) => d.hits), 1);
  const activeHours = b.byHour.filter((h) => h.hits > 0);
  const hFrom = activeHours.length ? Math.min(...activeHours.map((h) => h.h)) : 0;
  const hTo = activeHours.length ? Math.max(...activeHours.map((h) => h.h)) : 23;
  const hourSpan = b.byHour.slice(hFrom, hTo + 1);
  const maxHour = Math.max(...hourSpan.map((h) => h.hits), 1);
  const fmtHr = (h) => `${h % 12 || 12}${h < 12 ? "a" : "p"}`;
  const owner = b.people[0];
  const byline = [
    bandLabel(b.severityBand),
    b.status === "new" ? "new this period" : b.status === "recurring" ? `recurring · ${b.prevHits}× last period` : b.kind === "waiting" ? "waiting on someone" : "recurring failure",
    b.sinceLabel ? `since ${b.sinceLabel}` : null,
    owner ? `${owner.name.split(" ")[0]} owns it` : null,
    !b.aiWritten ? (live ? "writing now" : "write-up landing") : null,
  ].filter(Boolean).join(" · ");
  const facts = [
    [fmtH(b.hoursCaught), "touched"],
    [String(b.people.length), b.people.length === 1 ? "person" : "people"],
    [`${b.occurrences}×`, `on ${b.daysHit} days`],
    [b.sharePct != null ? `${b.sharePct}%` : "—", "of hours"],
  ];
  const place = [b.apps.map((a) => a.app).slice(0, 2).join(" · "), b.category].filter(Boolean).join(" · ");
  return shell(
    <>
      <div className="oi3-case-h">
        <Sev band={b.severityBand} n={b.severity} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3>{liveField("title") != null ? <>{liveField("title")}<Caret c={c} /></> : b.title}</h3>
          <p className="oi3-byline"><em style={{ color: ink, fontStyle: "normal", fontWeight: 650 }}>{bandLabel(b.severityBand)}</em> · {byline.replace(`${bandLabel(b.severityBand)} · `, "")}</p>
        </div>
      </div>
      <div className="oi3-facts">
        {facts.map(([n, l]) => <div key={l}><b>{n}</b><span>{l}</span></div>)}
      </div>
      <div className="oi3-proof">
        <div className="oi3-proof-side">
          <p className="oi3-h">When it hit</p>
          <div className="oi3-daysbar">
            {b.byDay.map((d) => {
              const on = d.hits > 0;
              return (
                <i key={d.date} title={`${d.date} · ${d.hits}× · ${d.people} people · ${d.hours}h`} style={{ height: on ? `${Math.max(18, (d.hits / maxDay) * 100)}%` : "4%", background: on ? c : alpha(c, 0.14) }}>
                  {on ? <span>{d.hits}×</span> : null}
                </i>
              );
            })}
          </div>
          <div className="oi3-dayslab">
            {b.byDay.map((d) => <span key={d.date}>{new Date(`${d.date}T12:00:00`).toLocaleDateString(undefined, { weekday: "short" }).slice(0, 2)}</span>)}
          </div>
          {hourSpan.length > 1 ? (
            <>
              <div className="oi3-hourbar">
                {hourSpan.map((h) => <i key={h.h} title={`${fmtHr(h.h)} · ${h.hits}×`} style={{ height: `${Math.max(8, (h.hits / maxHour) * 100)}%`, background: h.hits ? c : alpha(c, 0.14) }} />)}
              </div>
              <div className="oi3-hlab"><span>{fmtHr(hFrom)}</span><span>time of day</span><span>{fmtHr(hTo)}</span></div>
            </>
          ) : null}
          <p className="oi3-h">Who it hit</p>
          {b.people.map((p) => (
            <button key={p.id} type="button" className="oi3-person" onClick={() => onOpenPerson(p)}>
              <Avatar name={p.name} size={26} />
              <span className="oi3-who">{p.name}{p.position ? <span>{p.position}</span> : null}</span>
              <span className="oi3-n">{p.hits}× · {fmtH(p.hoursCaught)}</span>
            </button>
          ))}
          {place ? <p className="oi3-place">{place}</p> : null}
        </div>
        <div>
          <p className="oi3-h">Evidence · analysed, with time and person</p>
          <Points
            items={evidenceSummary(b)}
            live={liveField("problem") ?? liveField("impact")}
            c={c}
          />
          <p className="oi3-h" style={{ marginTop: 6 }}>Hits</p>
          <EvidenceTrail b={b} />
          <div className="oi3-do">
            <p className="oi3-h">Next step{b.effort ? ` · ${b.effort} effort` : ""}</p>
            <p>{liveField("recommendation") != null ? <>{liveField("recommendation")}<Caret c={green} /></> : (b.recommendation || `${(b.people[0]?.name || "The owner").split(" ")[0]} acts on this with the evidence trail this week.`)}</p>
            {b.people.slice(0, 2).map((p) => (
              <button key={p.id} type="button" className="oi3-do-link" onClick={() => onOpenPerson(p, p.days[0])}>
                Open {p.name.split(" ")[0]}'s {p.days[0] ? new Date(`${p.days[0]}T12:00:00`).toLocaleDateString(undefined, { weekday: "long" }) : "work"}
              </button>
            ))}
          </div>
        </div>
      </div>
      <span style={{ display: "none" }}>{k.blue}{ppr.card}{m?.period?.daysInRange}</span>
    </>
  );
}

/* ── idle hero (before the first run) ───────────────────────────────────── */
function IdleHero({ onRun, periodLabel, scopeLabel }) {
  const { t, green, bandColor, bandInk } = usePalette();
  const items = [
    { n: "01", band: "act", title: "Verified blockers", pts: ["Same identifier on 2+ days", "Evidence trail with time and person", "One named next step"] },
    { n: "02", band: "ok", title: "Hours you can give back", pts: ["Same work · one person · 4+ hours", "Hours back per week", "A template, export, or short script"] },
    { n: "03", band: "week", title: "People signals", pts: ["Went quiet mid-period", "Sharp drop vs their own last period", "Overload · 9h+ days"] },
    { n: "04", band: "monitor", title: "Where the time went", pts: ["Measured tool time", "Comms load per person"] },
    { n: "05", band: "monitor", title: "What changed", pts: ["New and resolved blockers", "Trend flips vs last period"] },
  ];
  return (
    <Card className="oi3-idle" style={{ marginTop: 14 }} pad="0">
      <div className="oi3-idle-grid">
        <div className="oi3-idle-lead">
          <div className="oi3-h" style={{ marginTop: 0 }}>Ready · {scopeLabel} · {periodLabel}</div>
          <h2>See what's blocking your company</h2>
          <p>Every number is measured from tracked activity. The model only writes the words. The first read fills in section by section.</p>
          <div className="oi3-idle-facts">
            <div><b>≈ 20s</b><span>first read</span></div>
            <div><b>Measured</b><span>never invented</span></div>
            <div><b>Zero ok</b><span>a valid result</span></div>
          </div>
          <div className="oi3-idle-acts">
            <Button variant="primary" leftIcon={<Zap size={15} />} onClick={() => onRun("7d")}>Analyze last 7 days</Button>
            <Button variant="secondary" onClick={() => onRun("30d")}>Last 30 days</Button>
            <Button variant="secondary" onClick={() => onRun(null)}>Use current filters</Button>
          </div>
          <p className="oi3-prov" style={{ marginTop: 18 }}><i className="oi3-dot" style={{ background: green }} />Nothing is invented. Zero blockers is shown as such.</p>
        </div>
        <aside className="oi3-idle-menu" aria-label="What the report covers">
          <p className="oi3-h" style={{ marginTop: 0 }}>What you'll get</p>
          <ol>
            {items.map((it) => (
              <li key={it.n}>
                <span className="oi3-idle-n" style={{ color: bandInk(it.band), background: alpha(bandColor(it.band), 0.14) }}>{it.n}</span>
                <div>
                  <b>{it.title}</b>
                  <ul>{it.pts.map((p) => <li key={p}>{p}</li>)}</ul>
                </div>
              </li>
            ))}
          </ol>
        </aside>
      </div>
    </Card>
  );
}

/* ── people signals ─────────────────────────────────────────────────────── */
function Spark({ vals, c }) {
  const max = Math.max(...vals, 0.1);
  return <span style={{ display: "inline-flex", gap: 2, alignItems: "flex-end", height: 22 }}>{vals.map((v, i) => <b key={i} style={{ width: 6, height: `${Math.max(10, (v / max) * 100)}%`, background: c, opacity: i === vals.length - 1 ? 1 : 0.55, borderRadius: 2 }} />)}</span>;
}
function PersonRow({ c, p, sub, right, onOpen }) {
  const { t } = usePalette();
  return (
    <button type="button" className="oi3-sigrow" onClick={onOpen} style={{ color: "inherit" }}>
      <Avatar name={p.name} size={30} />
      <span style={{ flex: 1, minWidth: 0 }}><b style={{ display: "block", fontSize: 13.5 }}>{p.name}</b><span style={{ display: "block", fontSize: 12, color: t.c.textSecondary }}>{sub}</span></span>
      {right}
      <ArrowRight size={14} style={{ color: c, flexShrink: 0 }} aria-hidden="true" />
    </button>
  );
}
function PeopleSignals({ m, onOpenPerson, pending }) {
  const { t, k, red, orange, green } = usePalette();
  if (!m) {
    if (!pending) return null;
    return (
      <Card c={k.blue} style={{ marginTop: 14 }}>
        <SecHead c={k.blue} kicker="Computed from each person's own pattern" title="People signals" right={<SkChips n={3} w={70} />} />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 14 }}>
          {["Went quiet mid-period", "Sharp drop vs own last period", "Overload · 9h+ days"].map((l) => <div key={l}><div style={{ ...t.font.microLabel, color: t.c.textSecondary, marginBottom: 8 }}>{l}</div><SkRows n={1} h={56} /></div>)}
        </div>
      </Card>
    );
  }
  const { quiet, drops, overload } = m.peopleSignals;
  if (!quiet.length && !drops.length && !overload.length) return null;
  return (
    <Card c={k.blue} style={{ marginTop: 14 }}>
      <SecHead c={k.blue} kicker="Computed from each person's own pattern" title="People signals"
        tip="Went quiet: 2+ active days then nothing for the final 2+ complete working days. Sharp drop: ≤ −50% vs the person's own previous period (≥8h base). Overload: 9h+ per active day over 3+ days."
        right={<>{quiet.length ? <Pill c={k.gold}>{quiet.length} quiet</Pill> : null}{drops.length ? <Pill c={red}>{drops.length} sharp drop</Pill> : null}{overload.length ? <Pill c={orange}>{overload.length} overload</Pill> : null}</>} />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 14 }}>
        {quiet.length ? <div><div style={{ ...t.font.microLabel, color: t.c.textSecondary, marginBottom: 8 }}>Went quiet mid-period</div><div style={{ display: "flex", flexDirection: "column", gap: 6 }}>{quiet.map((p) => <PersonRow key={p.id} c={k.gold} p={p} onOpen={() => onOpenPerson(p, p.lastActiveDay)} sub={`${p.activeDays} active days · silent since ${p.lastActiveLabel}`} right={<Pill c={k.gold}>check in</Pill>} />)}</div></div> : null}
        {drops.length ? <div><div style={{ ...t.font.microLabel, color: t.c.textSecondary, marginBottom: 8 }}>Sharp drop vs own last period</div><div style={{ display: "flex", flexDirection: "column", gap: 6 }}>{drops.map((p) => <PersonRow key={p.id} c={red} p={p} onOpen={() => onOpenPerson(p)} sub={`${fmtH(p.prevHours)} → ${fmtH(p.hours)} (${p.pct}%)`} right={<Spark vals={p.byDay} c={red} />} />)}</div></div> : null}
        {overload.length ? <div><div style={{ ...t.font.microLabel, color: t.c.textSecondary, marginBottom: 8 }}>Overload · 9h+ days</div><div style={{ display: "flex", flexDirection: "column", gap: 6 }}>{overload.map((p) => <PersonRow key={p.id} c={orange} p={p} onOpen={() => onOpenPerson(p)} sub={`${p.perDay}h/day over ${p.activeDays} days${p.idleShare != null ? ` · ${p.idleShare}% idle` : ""}`} right={<Pill c={orange}>at risk</Pill>} />)}</div></div> : null}
      </div>
      <span style={{ display: "none" }}>{green}</span>
    </Card>
  );
}

/* ── time + departments ─────────────────────────────────────────────────── */
function TimeSpent({ m, pending }) {
  const { t, k, ppr } = usePalette();
  const ts = m?.timeSpent;
  if (!ts || !ts.available) {
    if (!pending || ts) return null;
    return (
      <Card c={k.blue}>
        <SecHead c={k.blue} kicker="Measured tool time" title="Where the time went" right={<Shim h={26} w={110} r={999} />} />
        <SkBars n={5} /><SkLabel /><SkChips n={2} w={120} />
      </Card>
    );
  }
  const max = Math.max(...ts.tools.map((x) => x.hours), 0.1);
  return (
    <Card>
      <SecHead kicker="Measured tool time" title={`${fmtH(ts.totalHours)} across ${ts.tools.length} tools`} tip="Active time per application, measured directly. Communication apps are tagged so the comms load per person can be read off." />
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {ts.tools.map((x) => {
          const c = k.blue;
          return (
            <div key={x.name} style={{ display: "grid", gridTemplateColumns: "140px 1fr 52px 40px 64px", gap: 12, alignItems: "center", padding: "8px 0", borderTop: `1px solid ${t.c.border}`, minHeight: 40 }}>
              <span style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 650, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={x.name}><Tool name={x.name} size={18} c={c} />{x.name}</span>
              <div><Bar c={c} pct={(x.hours / max) * 100} /></div>
              <span style={{ width: 56, textAlign: "right", fontWeight: 700, fontVariantNumeric: "tabular-nums", fontSize: 13 }}>{fmtH(x.hours)}</span>
              <span style={{ width: 44, textAlign: "right", fontSize: 12, fontWeight: 700, color: t.c.textSecondary, fontVariantNumeric: "tabular-nums" }}>{x.sharePct}%</span>
              {x.category === "comms" ? <Pill c={k.violet}>comms</Pill> : <span style={{ width: 62 }} />}
            </div>
          );
        })}
      </div>
      {ts.commsPeople.length ? <><Label c={k.violet}>Comms load · share of each person's tool time</Label><div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>{ts.commsPeople.map((p) => <Chip key={p.id}><Avatar name={p.name} size={20} />{p.name.split(" ")[0]} · {p.sharePct}% · {fmtH(p.hours)}</Chip>)}</div></> : null}
      <span style={{ display: "none" }}>{ppr.well}</span>
    </Card>
  );
}
function Departments({ signals, scopedDept, onScope, pending }) {
  const { t, k, green, red } = usePalette();
  const list = signals?.departments || [];
  if (!list.length || (list.length === 1 && list[0].name === "Unassigned")) {
    if (!pending || signals) return null;
    return (
      <Card c={k.blue}>
        <SecHead c={k.blue} kicker="Same read, per department" title="Department lens" right={<Shim h={26} w={160} r={999} />} />
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr>{["Department", "People", "Hours", "Blockers", "Quiet", "Health"].map((h) => <th key={h} style={{ textAlign: "left", ...t.font.microLabel, color: t.c.textSecondary, padding: "0 10px 8px" }}>{h}</th>)}</tr></thead>
          <tbody>{[0, 1, 2, 3].map((i) => <tr key={i}>{[110, 40, 60, 28, 28, 70].map((w, j) => <td key={j} style={{ padding: 10, borderTop: `1px solid ${t.c.border}` }}><Shim h={12} w={w} /></td>)}</tr>)}</tbody>
        </table>
      </Card>
    );
  }
  return (
    <Card c={k.blue}>
      <SecHead c={k.blue} kicker="Same read, per department" title="Department lens" tip="Departments come from each person's profile. Health uses the same formula as the company score, on that department's own numbers." right={<Chip>click one to scope the page</Chip>} />
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 460 }}>
          <thead><tr>{["Department", "People", "Hours", "Blockers", "Quiet", "Health"].map((h) => <th key={h} style={{ textAlign: "left", ...t.font.microLabel, color: t.c.textSecondary, padding: "0 10px 8px" }}>{h}</th>)}</tr></thead>
          <tbody>
            {list.map((d) => {
              const hc = d.health >= 70 ? green : d.health >= 45 ? k.gold : red;
              const sel = scopedDept === d.name;
              return (
                <tr key={d.name} onClick={() => d.name !== "Unassigned" && onScope(sel ? null : d.name)} style={{ cursor: d.name === "Unassigned" ? "default" : "pointer", background: sel ? alpha(k.teal, 0.08) : "transparent" }}>
                  <td style={{ padding: 10, borderTop: `1px solid ${t.c.border}`, fontWeight: 700, fontSize: 13 }}>{d.name}</td>
                  <td style={{ padding: 10, borderTop: `1px solid ${t.c.border}`, fontVariantNumeric: "tabular-nums" }}>{d.active} / {d.people}</td>
                  <td style={{ padding: 10, borderTop: `1px solid ${t.c.border}`, fontVariantNumeric: "tabular-nums" }}>{fmtH(d.hours)} <span style={{ color: d.trendPct == null ? t.c.textTertiary : d.trendPct >= 0 ? green : red, fontWeight: 700, fontSize: 12 }}>{d.trendPct == null ? "" : `${d.trendPct >= 0 ? "▲" : "▼"}${Math.abs(d.trendPct)}%`}</span></td>
                  <td style={{ padding: 10, borderTop: `1px solid ${t.c.border}` }}>{d.blockers ? <Pill c={red}>{d.blockers}</Pill> : <span style={{ color: green, fontWeight: 700, fontSize: 12 }}>none</span>}</td>
                  <td style={{ padding: 10, borderTop: `1px solid ${t.c.border}` }}>{d.quiet ? <Pill c={k.gold}>{d.quiet}</Pill> : <span style={{ color: t.c.textTertiary, fontSize: 12 }}>—</span>}</td>
                  <td style={{ padding: 10, borderTop: `1px solid ${t.c.border}` }}><div style={{ display: "flex", alignItems: "center", gap: 8 }}><div style={{ width: 70 }}><Bar c={hc} pct={d.health} /></div><b style={{ fontSize: 12, color: hc, fontVariantNumeric: "tabular-nums" }}>{d.health}</b></div></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/* ── automation + coverage ──────────────────────────────────────────────── */
function Automations({ list, onOpenPerson, typing, pending }) {
  const { t, k, green, ppr } = usePalette();
  const [sel, setSel] = React.useState(null);
  const picked = React.useRef(false);
  const caseRef = React.useRef(null);
  const listRef = React.useRef(null);
  const sorted = React.useMemo(() => [...(list || [])].sort((a, b) => (b.hoursPerWeek || 0) - (a.hoursPerWeek || 0)), [list]);
  React.useEffect(() => {
    if (!sorted.length) return;
    if (!sel || !sorted.some((a) => a.id === sel)) setSel(sorted[0].id);
  }, [sorted, sel]);
  const typingId = typing?.kind === "automation" ? typing.id : null;
  React.useEffect(() => { if (typingId && !picked.current) setSel(typingId); }, [typingId]);
  const pick = (id) => {
    picked.current = Boolean(id);
    setSel(id);
    if (typeof window !== "undefined" && window.matchMedia("(max-width: 980px)").matches) {
      caseRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  };
  const onListKey = (e) => {
    if (!sorted.length) return;
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp" && e.key !== "Home" && e.key !== "End") return;
    e.preventDefault();
    const i = Math.max(0, sorted.findIndex((a) => a.id === sel));
    const next = e.key === "Home" ? 0 : e.key === "End" ? sorted.length - 1 : e.key === "ArrowDown" ? Math.min(sorted.length - 1, i + 1) : Math.max(0, i - 1);
    if (sorted[next]) {
      pick(sorted[next].id);
      listRef.current?.querySelectorAll("[data-aid]")[next]?.focus();
    }
  };

  if (!list || !list.length) {
    if (!pending || list) return null;
    return (
      <Card pad="22px 0 0">
        <div style={{ padding: "0 26px 8px" }}>
          <SecHead kicker="Same work · 4+ hours · 2+ days · one person" title="Hours you can give back" />
        </div>
        <div className="oi3-desk" style={{ minHeight: 360 }}>
          <aside className="oi3-desk-list"><div style={{ padding: "16px 20px" }}><SkRows n={4} h={56} avatar={false} /></div></aside>
          <article className="oi3-desk-case"><div style={{ padding: 26 }}><Shim h={20} w="55%" /><Shim h={12} w="40%" style={{ marginTop: 10 }} /><Shim h={72} r={12} style={{ marginTop: 18 }} /></div></article>
        </div>
      </Card>
    );
  }

  const total = sorted.reduce((s, a) => s + (a.hoursPerWeek || 0), 0);
  const a = sorted.find((x) => x.id === sel) || sorted[0];
  const tt = a && !a.aiWritten ? typingFor(typing, "automation", a.id) : null;
  const live = (f) => (tt && tt.field === f ? tt.text : null);
  const first = a.person.split(" ")[0];
  const hrs = Math.round(a.hoursPerWeek || 0);
  const seen = a.hoursSeen || parseInt(a.frequency, 10) || "—";
  const daysN = a.daysCount || a.days?.length || null;
  const ev = (a.evidence || []).filter((e) => e && !looksLikePath(e));
  const title = live("title") != null ? live("title") : displayAutoTitle(a);

  return (
    <Card pad="22px 0 0">
      <div style={{ padding: "0 26px 4px" }}>
        <div className="oi3-auto-hero">
          <SecHead
            kicker="Same work · 4+ hours · 2+ days · one person"
            title="Hours you can give back"
            tip="The same window or the same manual work recurring across separate hours and days for one person. Anything three or more people share is treated as shared UI and dropped."
          />
          <div className="oi3-auto-tot" aria-label={`About ${Math.round(total)} hours per week`}>
            <b>≈ {Math.round(total)}<small>h</small></b>
            <span>per week · {sorted.length} repeat{sorted.length === 1 ? "" : "s"}</span>
          </div>
        </div>
        {total > 0 ? (
          <div className="oi3-share" title={sorted.map((x) => `${x.person.split(" ")[0]} ${Math.round(x.hoursPerWeek)}h`).join(" · ")}>
            {sorted.map((x, i) => (
              <i key={x.id} style={{ flex: Math.max(x.hoursPerWeek || 0.4, 0.4), background: green, opacity: 1 - i * 0.16 }} />
            ))}
          </div>
        ) : null}
        <p className="oi3-share-cap">{sorted.slice(0, 3).map((x, i) => <span key={x.id}>{i ? " · " : null}<b>{x.person.split(" ")[0]}</b> {Math.round(x.hoursPerWeek)}h</span>)}{sorted.length > 3 ? ` · ${sorted.length - 3} more` : ""}</p>
      </div>
      <div className="oi3-desk" style={{ minHeight: 420 }}>
        <aside className="oi3-desk-list" ref={listRef} role="listbox" aria-label="Automation opportunities" tabIndex={0} onKeyDown={onListKey}>
          {sorted.map((row, i) => {
            const on = row.id === a.id;
            const rtt = !row.aiWritten ? typingFor(typing, "automation", row.id, "title") : null;
            return (
              <button key={row.id} type="button" data-aid={row.id} role="option" aria-selected={on} className={`oi3-brow${on ? " on" : ""}`} onClick={() => pick(row.id)}>
                <Hrs n={Math.round(row.hoursPerWeek || 0)} />
                <span style={{ minWidth: 0 }}>
                  <span className="oi3-brow-t">{rtt ? <>{rtt.text}<Caret c={green} /></> : displayAutoTitle(row)}</span>
                  <span className="oi3-brow-m">{row.person.split(" ")[0]} · {shortApp(row.tool) || "the tool"}{i === 0 ? " · biggest" : ""}</span>
                </span>
              </button>
            );
          })}
        </aside>
        <article ref={caseRef} className="oi3-desk-case" aria-live="polite">
          <div className="oi3-case-h">
            <Hrs n={hrs} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <h3>{live("title") != null ? <>{title}<Caret c={green} /></> : title}</h3>
              <p className="oi3-byline">{first} owns the repeat · {shortApp(a.tool) || "the tool"}{daysN ? ` · ${daysN} days` : ""}</p>
            </div>
            {a.tool ? <Tool name={a.tool} size={22} c={k.blue} /> : null}
          </div>
          <div className="oi3-facts">
            <div><b>≈ {hrs}h</b><span>back per week</span></div>
            <div><b>{seen}</b><span>hours seen</span></div>
            <div><b>{first}</b><span>owns the repeat</span></div>
            <div><b>{daysN || "—"}</b><span>{daysN === 1 ? "day" : "days"}</span></div>
          </div>
          <div style={{ padding: "18px 26px 26px" }}>
            <p style={{ margin: 0, fontSize: 14, lineHeight: 1.55, color: t.c.textSecondary, maxWidth: "62ch" }}>
              {live("description") != null ? <>{live("description")}<Caret c={k.blue} /></> : a.description}
            </p>
            {a.days?.length ? (
              <div className="oi3-daychips">
                {a.days.map((d) => <span key={d}>{new Date(`${d}T12:00:00`).toLocaleDateString(undefined, { weekday: "short" })}</span>)}
              </div>
            ) : null}
            {ev.length ? <p className="oi3-place" style={{ marginTop: 12 }}>{ev.join(" · ")}</p> : a.where && !looksLikePath(a.where) ? <p className="oi3-place" style={{ marginTop: 12 }}>{a.where}</p> : null}
            <div className="oi3-do">
              <p className="oi3-h">Next step</p>
              <p>{live("recommendation") != null ? <>{live("recommendation")}<Caret c={green} /></> : (a.recommendation || `${first} replaces the repeat with a template or script this week.`)}</p>
              <button type="button" className="oi3-do-link" onClick={() => onOpenPerson({ id: a.userId, name: a.person })}>Open {first}'s work</button>
            </div>
          </div>
          <span style={{ display: "none" }}>{ppr.card}</span>
        </article>
      </div>
    </Card>
  );
}
function Coverage({ m, onOpenPerson, pending }) {
  const { t, k, green } = usePalette();
  if (!m) {
    if (!pending) return null;
    return (
      <Card c={k.blue}>
        <SecHead c={k.blue} kicker="Who the numbers cover" title="Coverage" right={<Shim h={26} w={120} r={999} />} />
        <div style={{ ...t.font.microLabel, color: t.c.textSecondary, marginBottom: 6 }}>Stopped reporting</div><SkRows n={1} h={56} />
        <SkLabel /><SkChips n={2} w={110} />
      </Card>
    );
  }
  const { stopped, notExpected } = m.coverage;
  if (!stopped.length && !notExpected.count) return null;
  return (
    <Card c={k.blue}>
      <SecHead c={k.blue} kicker="Who the numbers cover" title="Coverage" tip="Stopped reporting: had data in the last 30 days but none in this period. Not expected to track: no data for 30+ days — excluded from every percentage." right={<Chip>{m.kpis.peopleActive} of {m.kpis.peopleExpected} expected</Chip>} />
      {stopped.length ? <><div style={{ ...t.font.microLabel, color: k.gold, marginBottom: 6 }}>Stopped reporting · had data in the last 30 days</div><div style={{ display: "flex", flexDirection: "column", gap: 6 }}>{stopped.map((p) => <PersonRow key={p.id} c={k.gold} p={p} onOpen={() => onOpenPerson(p)} sub={`${p.position || "—"} · last data ${p.lastDataDaysAgo} day${p.lastDataDaysAgo === 1 ? "" : "s"} ago`} right={<Pill c={k.blue}>nudge</Pill>} />)}</div></> : null}
      {notExpected.count ? <><Label c={t.c.textSecondary}>Not expected to track · no data for 30+ days</Label><div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>{notExpected.people.slice(0, 12).map((p) => <Chip key={p.id}><Avatar name={p.name} size={18} />{p.name}{p.position ? ` (${p.position})` : ""}</Chip>)}</div></> : null}
      <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${t.c.border}`, fontSize: 11.5, color: t.c.textSecondary, display: "flex", gap: 8, alignItems: "center" }}><i style={{ width: 7, height: 7, borderRadius: 99, background: green }} />Excluded from every percentage above, so coverage isn't punished for people who never track.</div>
    </Card>
  );
}

/* ── the page ───────────────────────────────────────────────────────────── */
export default function OrgIntelligenceV3() {
  const { t, k, ppr, red, green } = usePalette();
  const { user } = useAuth();
  const navigate = useNavigate();
  const stream = useOrgIntelStream();
  const [member, setMember] = React.useState(null);
  const [includeTeam, setIncludeTeam] = React.useState(true);
  const [dept, setDept] = React.useState(null);
  const [range, setRange] = React.useState(() => presetToRange("7d"));
  const [users, setUsers] = React.useState([]);
  const [departments, setDepartments] = React.useState([]);
  const [selectedId, setSelectedId] = React.useState(null);
  const [started, setStarted] = React.useState(false);
  const userPicked = React.useRef(false);

  React.useEffect(() => {
    const token = localStorage.getItem("token");
    const h = { headers: { Authorization: `Bearer ${token}` } };
    fetch(`${SERVER_URL}/api/v1/saved-summaries?limit=1`, h).then((r) => r.json()).then((j) => { if (j?.success && j.data?.accessibleUsers) setUsers(j.data.accessibleUsers); }).catch(() => {});
    fetch(`${SERVER_URL}/api/v1/org-intelligence-v3/departments`, h).then((r) => r.json()).then((j) => { if (j?.success) setDepartments(j.data || []); }).catch(() => {});
  }, [user]);

  const memberOptions = React.useMemo(() => [{ value: null, label: "Entire company" }, ...users.map((u) => ({ value: u._id || u.id, label: `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email || "Member" }))], [users]);
  const deptOptions = React.useMemo(() => [{ value: null, label: "All departments" }, ...departments.map((d) => ({ value: d.name, label: `${d.name} · ${d.people}` }))], [departments]);

  const analyze = React.useCallback((force = false, overrides = {}) => {
    const r = overrides.range || range;
    const d = overrides.dept !== undefined ? overrides.dept : dept;
    setStarted(true); setSelectedId(null); userPicked.current = false;
    stream.run({ userId: member || null, includeTeam: member ? includeTeam : false, department: d || null, startDate: fmtDate(r.start), endDate: fmtDate(r.end), forceRegenerate: force });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member, includeTeam, dept, range]);

  const m = stream.metrics;
  const blockerList = React.useMemo(() => (stream.blockers?.blockers || []).map((b) => stream.ai.blockers[b.id] ? { ...b, ...stream.ai.blockers[b.id] } : b), [stream.blockers, stream.ai.blockers]);
  const automationList = React.useMemo(() => (stream.signals?.automations || []).map((a) => stream.ai.automations[a.id] ? { ...a, ...stream.ai.automations[a.id] } : a), [stream.signals, stream.ai.automations]);
  const firstId = blockerList[0]?.id || null;
  React.useEffect(() => { if (firstId && !selectedId) setSelectedId(firstId); }, [firstId, selectedId]);
  const selected = blockerList.find((b) => b.id === selectedId) || null;
  const typingId = stream.typing?.kind === "blocker" ? stream.typing.id : null;
  React.useEffect(() => {
    if (typingId && stream.streaming && !userPicked.current) setSelectedId(typingId);
  }, [typingId, stream.streaming]);
  const selectBlocker = React.useCallback((id) => {
    userPicked.current = Boolean(id);
    setSelectedId(id);
  }, []);
  const health = stream.blockers?.health || stream.report?.health || null;
  const changes = stream.signals?.changes ?? stream.report?.changes ?? null;

  const openPerson = (p, day) => {
    if (!p?.id) return;
    const qs = new URLSearchParams({ name: p.name || "" });
    if (day) { qs.set("start", day); qs.set("end", day); }
    navigate(`/dashboard/graphical/person/${p.id}?${qs.toString()}`);
  };

  const periodLabel = m?.period?.label || `${range.start.toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${range.end.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
  const scopeLabel = member ? (includeTeam ? "person + team" : "one person") : dept ? `${dept} department` : "entire company";
  const pulse = [];
  if (m) {
    pulse.push(`${m.scope.people} people`);
    pulse.push(`${m.kpis.workingDays} working days`);
    if (stream.blockers) pulse.push(blockerList.length ? `${blockerList.length} verified blocker${blockerList.length === 1 ? "" : "s"}` : "no verified blockers");
    if (m.peopleSignals.quiet.length) pulse.push(`${m.peopleSignals.quiet.length} went quiet`);
    if (health) pulse.push(`health ${health.score}`);
    if (stream.cached) pulse.push(stream.cached.ageMinutes < 1 ? "cached just now" : `cached ${stream.cached.ageMinutes} min ago`);
  }

  return (
    <div className="oi3-page" style={{ minHeight: "100%", background: ppr.canvas, padding: "22px 26px 80px", fontFamily: t.font.family, color: t.c.text }}>
      <style>{`
@keyframes oi3spin{to{transform:rotate(360deg)}}
@keyframes oi3blink{50%{opacity:0}}
.oi3-page :focus-visible{outline:2px solid ${k.blue};outline-offset:2px;border-radius:4px}
.oi3-skip{position:absolute;left:-999px;top:10px}
.oi3-skip:focus{left:18px;z-index:40;background:${ppr.card};padding:8px 12px;border-radius:8px;color:${t.c.text};text-decoration:none;font-weight:600;font-size:13px}
.oi3-top{display:flex;align-items:flex-start;gap:20px;flex-wrap:wrap}
.oi3-top h1{margin:0;font-size:27px;font-weight:700;letter-spacing:-.5px;line-height:1.15}
.oi3-top .sub{margin:7px 0 0;font-size:13.5px;color:${t.c.textSecondary}}
.oi3-top .sub b{color:${t.c.text};font-weight:600}
.oi3-acts{margin-left:auto;display:flex;flex-direction:column;gap:10px;align-items:flex-end}
.oi3-pulse{display:flex;gap:8px;align-items:center;margin:16px 0 4px;font-size:12.5px;color:${t.c.textSecondary};flex-wrap:wrap}
.oi3-pulse b{color:${t.c.text};font-weight:600}
.oi3-pulse a{margin-left:auto;font-weight:600;color:${k.blue};text-decoration:none}
.oi3-pulse a:hover{text-decoration:underline}
.oi3-dot{width:8px;height:8px;border-radius:99px;flex:none}
.oi3-hero{display:grid;grid-template-columns:minmax(0,1.55fr) minmax(300px,.92fr);gap:16px;margin-top:14px}
.oi3-ai-flag{display:inline-flex;align-items:center;gap:6px;font-size:11px;font-weight:700;letter-spacing:.09em;text-transform:uppercase;color:${k.violet}}
.oi3-read-head{margin:14px 0 0;font-size:23px;font-weight:700;letter-spacing:-.5px;line-height:1.32;max-width:36ch}
.oi3-stat{font-weight:750;font-variant-numeric:tabular-nums;color:${t.c.text};letter-spacing:-.2px}
.oi3-read-meta{margin:10px 0 0;font-size:12.5px;color:${t.c.textSecondary};line-height:1.45}
.oi3-beats{margin:18px 0 0;padding:0;list-style:none;display:grid;grid-template-columns:1fr 1fr;gap:10px}
.oi3-beat{margin:0;padding:13px 14px 14px;border-radius:12px;background:${ppr.well};border:1px solid ${t.c.border};min-height:96px}
.oi3-beat-k{display:inline-flex;align-items:center;gap:7px;font-size:10.5px;font-weight:750;letter-spacing:.08em;text-transform:uppercase}
.oi3-beat-k i{width:8px;height:8px;border-radius:99px;flex:none}
.oi3-beat-b{display:block;margin-top:8px;font-size:13.5px;line-height:1.5;color:${t.c.textSecondary}}
.oi3-beat-b .oi3-stat{color:${t.c.text}}
.oi3-prov{margin:16px 0 0;padding-top:12px;border-top:1px solid ${t.c.border};font-size:11.5px;color:${t.c.textSecondary};display:flex;gap:8px;align-items:center}
.oi3-health-stack{display:flex;flex-direction:column;align-items:center;text-align:center;margin-top:6px}
.oi3-health-word{margin:2px 0 0;font-size:26px;font-weight:750;letter-spacing:-.5px;line-height:1.1}
.oi3-health-delta{margin:6px 0 0;font-size:12.5px;color:${t.c.textSecondary}}
.oi3-health-ring{margin:14px 0 6px}
.oi3-hrows{width:100%;text-align:left;margin-top:8px}
.oi3-hrow{display:flex;align-items:baseline;justify-content:space-between;gap:10px;padding:8px 0 0;font-size:13px}
.oi3-hpen{padding-bottom:8px;border-top:1px solid ${t.c.border}}
.oi3-hpen:first-of-type{border-top:0}
.oi3-hrow-cap{border-top:0;padding:0 0 6px;font-size:11px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:${t.c.textTertiary}}
.oi3-hrow .l{color:${t.c.textSecondary}}
.oi3-hrow .l b{color:${t.c.text};font-weight:600}
.oi3-hrow .n{font-variant-numeric:tabular-nums;font-weight:650;color:${t.c.text}}
.oi3-htrack{display:block;height:6px;border-radius:99px;background:${alpha(t.c.text, 0.08)};overflow:hidden;margin-top:6px}
.oi3-htrack i{display:block;height:100%;border-radius:99px;max-width:100%}
.oi3-hfoot{margin:8px 0 0;font-size:11.5px;color:${t.c.textTertiary};line-height:1.5;text-align:left;width:100%}
.oi3-idle-grid{display:grid;grid-template-columns:minmax(280px,1fr) minmax(300px,1.05fr);min-height:420px}
.oi3-idle-lead{padding:32px 34px 28px;display:flex;flex-direction:column}
.oi3-idle-lead h2{margin:10px 0 0;font-size:30px;font-weight:750;letter-spacing:-.7px;line-height:1.15;max-width:16ch}
.oi3-idle-lead>p{margin:12px 0 0;font-size:14.5px;line-height:1.55;color:${t.c.textSecondary};max-width:42ch}
.oi3-idle-facts{display:flex;margin:22px 0 0;border-top:1px solid ${t.c.border};border-bottom:1px solid ${t.c.border}}
.oi3-idle-facts>div{flex:1;padding:13px 16px 13px 0;border-right:1px solid ${t.c.border}}
.oi3-idle-facts>div:last-child{border-right:0;padding-right:0}
.oi3-idle-facts b{display:block;font-size:17px;font-weight:700;letter-spacing:-.3px}
.oi3-idle-facts span{display:block;margin-top:2px;font-size:11.5px;color:${t.c.textSecondary}}
.oi3-idle-acts{display:flex;gap:8px;flex-wrap:wrap;margin-top:22px}
.oi3-idle-menu{padding:28px 28px 24px;border-left:1px solid ${t.c.border};background:${ppr.well}}
.oi3-idle-menu>ol{margin:4px 0 0;padding:0;list-style:none}
.oi3-idle-menu>ol>li{display:grid;grid-template-columns:36px 1fr;gap:12px;padding:12px 0;border-top:1px solid ${t.c.border}}
.oi3-idle-menu>ol>li:first-child{border-top:0;padding-top:6px}
.oi3-idle-n{width:36px;height:36px;border-radius:10px;display:grid;place-items:center;font-size:12px;font-weight:750;font-variant-numeric:tabular-nums;flex:none}
.oi3-idle-menu b{display:block;font-size:14.5px;font-weight:650;letter-spacing:-.2px}
.oi3-idle-menu ul{margin:6px 0 0;padding:0;list-style:none}
.oi3-idle-menu ul li{position:relative;padding:2px 0 2px 12px;font-size:12.5px;line-height:1.45;color:${t.c.textSecondary}}
.oi3-idle-menu ul li::before{content:"·";position:absolute;left:0;color:${t.c.textTertiary};font-weight:700}
@media (max-width:980px){.oi3-hero{grid-template-columns:1fr}.oi3-beats{grid-template-columns:1fr}.oi3-read-head{font-size:20px;max-width:none}.oi3-idle-grid{grid-template-columns:1fr}.oi3-idle-menu{border-left:0;border-top:1px solid ${t.c.border}}.oi3-idle-lead h2{font-size:26px;max-width:none}}
.oi3-desk{display:grid;grid-template-columns:minmax(300px,38%) minmax(0,1fr);min-height:520px;border-top:1px solid ${t.c.border}}
.oi3-desk-list{border-right:1px solid ${t.c.border};overflow-y:auto;max-height:min(72vh,720px)}
.oi3-desk-case{min-width:0;overflow-y:auto;max-height:min(72vh,720px)}
.oi3-brow{display:grid;grid-template-columns:38px minmax(0,1fr);gap:12px;align-items:center;width:100%;text-align:left;padding:14px 20px;min-height:56px;border:0;border-top:1px solid ${t.c.border};background:transparent;cursor:pointer;font-family:inherit;color:inherit;transition:background 160ms ease}
.oi3-brow:first-of-type{border-top:0}
.oi3-brow:hover{background:${ppr.well}}
.oi3-brow.on{background:${alpha(k.blue, 0.1)}}
.oi3-brow-t{display:block;font-size:14px;font-weight:650;letter-spacing:-.2px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.oi3-brow-m{display:block;font-size:12px;color:${t.c.textSecondary};margin-top:2px;font-variant-numeric:tabular-nums}
.oi3-brow-side{display:flex;align-items:center;gap:10px;margin-top:6px}
.oi3-faces{display:flex}
.oi3-faces > * + *{margin-left:-7px}
.oi3-case-h{padding:20px 26px 16px;border-bottom:1px solid ${t.c.border};display:flex;gap:14px;align-items:flex-start}
.oi3-case-h h3{margin:0;font-size:20px;font-weight:700;letter-spacing:-.35px;line-height:1.3;max-width:34ch}
.oi3-byline{margin:6px 0 0;font-size:13px;color:${t.c.textSecondary}}
.oi3-facts{display:flex;border-bottom:1px solid ${t.c.border}}
.oi3-facts>div{flex:1;padding:13px 20px;border-right:1px solid ${t.c.border}}
.oi3-facts>div:last-child{border-right:0}
.oi3-facts b{display:block;font-size:17px;font-weight:650;letter-spacing:-.3px}
.oi3-facts span{display:block;font-size:11.5px;color:${t.c.textSecondary};margin-top:2px}
.oi3-proof{padding:20px 26px 26px;display:grid;grid-template-columns:minmax(240px,2fr) minmax(280px,3fr);gap:0 36px;align-items:start}
.oi3-proof-side{position:sticky;top:16px}
.oi3-h{font-size:11px;font-weight:700;letter-spacing:.09em;text-transform:uppercase;color:${t.c.textTertiary};margin:18px 0 9px}
.oi3-h:first-child{margin-top:0}
.oi3-daysbar{display:flex;gap:2px;align-items:flex-end;height:64px;border-bottom:1px solid ${t.c.border};padding:0 2px;margin-top:22px}
.oi3-daysbar i{flex:1;max-width:24px;border-radius:4px 4px 0 0;position:relative;display:block}
.oi3-daysbar i span{position:absolute;top:-17px;left:0;right:0;text-align:center;font-size:10.5px;font-weight:700;color:${t.c.textSecondary};font-variant-numeric:tabular-nums}
.oi3-dayslab{display:flex;gap:2px;padding:5px 2px 0}
.oi3-dayslab span{flex:1;max-width:24px;text-align:center;font-size:10px;color:${t.c.textTertiary};font-variant-numeric:tabular-nums}
.oi3-hourbar{display:flex;gap:2px;align-items:flex-end;height:30px;margin-top:16px}
.oi3-hourbar i{flex:1;border-radius:2px 2px 0 0;display:block}
.oi3-hlab{display:flex;justify-content:space-between;font-size:10.5px;color:${t.c.textTertiary};margin-top:4px;font-variant-numeric:tabular-nums}
.oi3-person{display:flex;align-items:center;gap:10px;padding:8px 0;min-height:44px;border:0;border-top:1px solid ${t.c.border};width:100%;background:transparent;cursor:pointer;font-family:inherit;color:inherit;text-align:left;transition:background 160ms ease}
.oi3-person:first-of-type{border-top:0}
.oi3-person:hover{background:${ppr.well}}
.oi3-who{font-weight:600;font-size:13.5px}
.oi3-who span{font-weight:400;color:${t.c.textSecondary};margin-left:6px}
.oi3-n{margin-left:auto;font-variant-numeric:tabular-nums;color:${t.c.textSecondary};font-size:12.5px}
.oi3-place{font-size:13px;color:${t.c.textSecondary};margin-top:8px}
.oi3-pts{margin:0 0 14px;padding:0;list-style:none}
.oi3-pts li{position:relative;padding:5px 0 5px 16px;font-size:13.5px;line-height:1.5;color:${t.c.textSecondary}}
.oi3-pts li::before{content:"";position:absolute;left:0;top:12px;width:6px;height:6px;border-radius:99px;background:${t.c.text};opacity:.28}
.oi3-trail{margin:0}
.oi3-led{display:grid;grid-template-columns:20px 1fr;gap:10px;padding:10px 0;border-top:1px solid ${t.c.border};font-size:13.5px}
.oi3-led:first-child{border-top:0;padding-top:2px}
.oi3-led-i{color:${t.c.textTertiary};font-variant-numeric:tabular-nums;font-size:12px;padding-top:2px}
.oi3-led-h{font-size:13.5px;line-height:1.4}
.oi3-led-h b{font-variant-numeric:tabular-nums}
.oi3-led-app{color:${t.c.textSecondary}}
.oi3-led-pts{margin:6px 0 0;padding:0;list-style:none}
.oi3-led-pts li{position:relative;padding:2px 0 2px 12px;font-size:12.5px;line-height:1.45;color:${t.c.textSecondary}}
.oi3-led-pts li::before{content:"·";position:absolute;left:0;top:0;color:${t.c.textTertiary};font-weight:700}
.oi3-led-w{color:${t.c.textSecondary};font-size:12.5px;margin-top:1px;line-height:1.5}
.oi3-do{margin-top:16px;padding:14px 16px;border-radius:12px;background:${alpha(green, 0.08)};border:1px solid ${alpha(green, 0.3)}}
.oi3-do .oi3-h{color:${green};margin:0 0 6px}
.oi3-do p{margin:0;font-size:14px;line-height:1.5;color:${t.c.text}}
.oi3-do-link{display:inline-block;margin-top:10px;margin-right:16px;font-size:13px;font-weight:600;color:${k.blue};background:none;border:0;padding:0;cursor:pointer;font-family:inherit}
.oi3-do-link:hover{text-decoration:underline}
.oi3-auto-hero{display:flex;align-items:flex-end;justify-content:space-between;gap:20px;flex-wrap:wrap}
.oi3-auto-hero>div{margin-bottom:0}
.oi3-auto-tot{text-align:right;flex:none;padding-bottom:2px}
.oi3-auto-tot b{display:block;font-size:40px;font-weight:750;letter-spacing:-1.4px;line-height:1;font-variant-numeric:tabular-nums;color:${green}}
.oi3-auto-tot small{font-size:16px;font-weight:650;letter-spacing:-.2px;margin-left:1px}
.oi3-auto-tot span{display:block;margin-top:5px;font-size:12px;color:${t.c.textSecondary}}
.oi3-share{display:flex;height:8px;border-radius:99px;overflow:hidden;gap:2px;margin-top:2px}
.oi3-share i{display:block;height:100%;min-width:8px}
.oi3-share-cap{margin:8px 0 10px;font-size:12.5px;color:${t.c.textSecondary}}
.oi3-share-cap b{color:${t.c.text};font-weight:600}
.oi3-daychips{display:flex;gap:6px;flex-wrap:wrap;margin-top:14px}
.oi3-daychips span{font-size:12px;font-weight:600;padding:5px 10px;border-radius:8px;background:${ppr.well};color:${t.c.textSecondary}}
.oi3-sigrow{display:flex;align-items:center;gap:11px;width:100%;padding:10px 4px;min-height:52px;border:0;border-top:1px solid ${t.c.border};background:transparent;cursor:pointer;font-family:inherit;text-align:left;border-radius:8px;transition:background 160ms ease}
.oi3-sigrow:first-of-type{border-top:0}
.oi3-sigrow:hover{background:${ppr.well}}
@media (max-width:980px){
  .oi3-desk{grid-template-columns:1fr;min-height:0}
  .oi3-desk-list{border-right:0;border-bottom:1px solid ${t.c.border};max-height:280px}
  .oi3-desk-case{max-height:none}
  .oi3-proof{grid-template-columns:1fr}
  .oi3-proof-side{position:static;top:auto}
  .oi3-facts{flex-wrap:wrap}
  .oi3-facts>div{min-width:40%;border-bottom:1px solid ${t.c.border}}
}
@media (prefers-reduced-motion:reduce){
  .oi3-page *,.oi3-page *::before,.oi3-page *::after{animation:none!important;transition:none!important}
}
`}
      </style>
      <ClassicMotion />
      <WowMotion />
      <div style={{ maxWidth: t.layout.contentMax, margin: "0 auto" }}>
        <a className="oi3-skip" href="#oi3-blockers">Skip to blockers</a>
        <div style={{ marginBottom: 18 }}><Breadcrumb items={[{ label: "Insights", to: "/dashboard/work-summary-new" }, { label: "Org Intelligence" }]} /></div>

        <div className="oi3-top">
          <div>
            <h1>Org Intelligence</h1>
            <p className="sub">What's blocking <b>{scopeLabel}</b> · <b>{periodLabel}</b>{m?.period?.timezone ? ` · ${m.period.timezone}` : ""}</p>
          </div>
          <div className="oi3-acts">
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
              {started && !stream.streaming ? <Button variant="secondary" leftIcon={<RefreshCw size={14} />} onClick={() => analyze(true)}>Refresh</Button> : null}
              {stream.streaming ? <Button variant="secondary" leftIcon={<Square size={14} />} onClick={stream.stop}>Stop</Button> : <Button variant="primary" leftIcon={<Zap size={15} />} onClick={() => analyze(false)}>Analyze</Button>}
            </div>
            <div className="tg-filterbar">
              <ToolSelect icon={<Users size={13} />} value={member} options={memberOptions} onChange={(v) => { setMember(v ?? null); }} placeholder="Entire company" panelWidth={250} placement="bottomRight" />
              {member ? <><span className="tg-tool-sep" /><button type="button" className="tg-tool" onClick={() => setIncludeTeam((x) => !x)} style={includeTeam ? { color: t.c.primary } : undefined}><CheckCircle2 size={13} style={{ opacity: includeTeam ? 1 : 0.3 }} /><span className="tg-tool-value">+ direct reports</span></button></> : null}
              {departments.length ? <><span className="tg-tool-sep" /><ToolSelect icon={<Building2 size={13} />} value={dept} options={deptOptions} onChange={(v) => setDept(v ?? null)} placeholder="All departments" panelWidth={240} placement="bottomRight" /></> : null}
              <span className="tg-tool-sep" />
              <DateRangeTool range={range} onChange={setRange} layoutId="orgIntelV3Range" panelWidth={380} presets={[
                { key: "1d", label: "1 day" },
                { key: "2d", label: "2 days" },
                { key: "3d", label: "3 days" },
                { key: "7d", label: "7 days" },
                { key: "14d", label: "14 days" },
                { key: "30d", label: "30 days" },
                { key: "month", label: "This month" },
                { key: "lastMonth", label: "Last month" },
              ]} />
            </div>
          </div>
        </div>

        {!started ? (
          <IdleHero periodLabel={periodLabel} scopeLabel={scopeLabel}
            onRun={(preset) => { if (preset) { const r = presetToRange(preset); setRange(r); analyze(false, { range: r }); } else analyze(false); }} />
        ) : (
          <>
            <GenBar steps={stream.steps} status={stream.status} streaming={stream.streaming} cached={stream.cached} report={stream.report} />
            {m && !stream.streaming ? (
              <div className="oi3-pulse">
                <i className="oi3-dot" style={{ background: blockerList.length ? red : green }} />
                <span>{pulse.map((bit, i) => <span key={bit}>{i ? <span aria-hidden="true"> · </span> : null}<b>{bit}</b></span>)}</span>
                <a href="#oi3-blockers">Jump to blockers</a>
              </div>
            ) : null}
            {stream.error ? (
              <Card style={{ marginTop: 14 }}>
                <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
                  <div>
                    <b>{/no tracked activity|no people/i.test(stream.error) ? "Nothing to read here" : "Analysis unavailable"}</b>
                    <div style={{ fontSize: 13, color: t.c.textSecondary, marginTop: 4 }}>{stream.error}</div>
                  </div>
                  <span style={{ marginLeft: "auto" }}><Button variant="secondary" onClick={() => analyze(true)}>Try again</Button></span>
                </div>
              </Card>
            ) : null}
            {(m || stream.streaming) && !stream.error ? (
              <>
                <div className="oi3-hero">
                  <Landing><Brief ai={stream.ai} blockers={stream.blockers} report={stream.report} streaming={stream.streaming} typing={stream.typing} /></Landing>
                  <Landing delay={80}><Health health={health} changes={changes} /></Landing>
                </div>
                {/* <Changes changes={changes} ready={Boolean(stream.signals || stream.report)} pending={stream.streaming} /> */}
                <KpiRow m={m} blockers={stream.blockers} pending={stream.streaming} />
                <div id="oi3-blockers" style={{ marginTop: 22 }}>
                  <Landing>
                    <Card pad="22px 0 0">
                      <div style={{ padding: "0 26px" }}>
                        <SecHead kicker="Verified only · same identifier · 2+ days" title={`Verified blockers${stream.blockers ? ` · ${blockerList.length}` : ""}`} tip="A failure needs strong phrasing plus a concrete identifier (file, host, error type, ticket) in the same clause; a wait needs explicit waiting/blocked phrasing. Either must recur on 2+ separate days. Bare 'error' never counts."
                          right={<span style={{ fontSize: 12, color: t.c.textTertiary, fontVariantNumeric: "tabular-nums" }}>severity 80+ act now · 60–79 this week · 40–59 monitor</span>} />
                      </div>
                      <BlockerDesk list={blockerList} selectedId={selectedId} onSelect={selectBlocker} streaming={stream.streaming && !stream.blockers} typing={stream.typing} selected={selected} m={m} onOpenPerson={openPerson} pending={stream.streaming && !stream.blockers} />
                    </Card>
                  </Landing>
                </div>
                <div id="oi3-automations" style={{ marginTop: 14 }}>
                  <Landing>
                    <Automations list={stream.signals || stream.report ? automationList : null} onOpenPerson={openPerson} typing={stream.typing} pending={stream.streaming} />
                  </Landing>
                </div>
                <PeopleSignals m={m} onOpenPerson={openPerson} pending={stream.streaming} />
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))", gap: 14, marginTop: 14 }}>
                  <TimeSpent m={m} pending={stream.streaming} />
                  <Departments signals={stream.signals || stream.report} scopedDept={dept} onScope={(d) => { setDept(d); analyze(false, { dept: d }); }} pending={stream.streaming} />
                </div>
                <div style={{ marginTop: 14 }}>
                  <Coverage m={m} onOpenPerson={openPerson} pending={stream.streaming} />
                </div>
                <div style={{ marginTop: 22, display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: t.c.textSecondary, flexWrap: "wrap" }}>
                  <i className="oi3-dot" style={{ background: green }} />Every fact on this page is measured from tracked activity. Nothing is invented; the model only writes the words.
                  {stream.report?.generatedAt ? <span style={{ marginLeft: "auto" }}><Clock size={12} /> generated {new Date(stream.report.generatedAt).toLocaleString()}</span> : null}
                </div>
              </>
            ) : null}
          </>
        )}
      </div>
      <AILimitModal isOpen={stream.limitReached} onClose={() => stream.setLimitReached(false)} />
    </div>
  );
}
